import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import React, { useState, useEffect } from 'react';
import {
  X, Wrench, CheckCircle, Clock, AlertTriangle, ArrowLeft,
  Calendar, Building2, FileText, Truck, Euro, List, ChevronDown
} from 'lucide-react';
import api from '../../api.js';
import {
  DURATION_PRESETS, DEFAULT_DURATION, formatDuration,
  computePickupTime, exceedsGarageClosing, GARAGE_LAST_DROPOFF
} from '../../utils/maintenanceDuration.js';
import { useFormat } from '../../i18n/format.js';

// 3-step lean workflow (2026-04-21). Legacy values map to the nearest
// canonical state so pre-migration rows still render a coherent stepper.
const STATUS_CONFIG = {
  created:        { label: 'Créée',    bg: 'bg-blue-100',    text: 'text-blue-800',    accent: '#3b82f6' },
  planned:        { label: 'Planifiée', bg: 'bg-amber-100',  text: 'text-amber-800',   accent: '#f59e0b' },
  completed:      { label: 'Terminée', bg: 'bg-emerald-100', text: 'text-emerald-800', accent: '#10b981' },
  cancelled:      { label: 'Annulée',  bg: 'bg-gray-100',    text: 'text-gray-500',    accent: '#6b7280' },
  // --- legacy aliases, displayed as their lean equivalent ---
  draft:          { label: 'Créée',    bg: 'bg-blue-100',    text: 'text-blue-800',    accent: '#3b82f6' },
  submitted:      { label: 'Créée',    bg: 'bg-blue-100',    text: 'text-blue-800',    accent: '#3b82f6' },
  manager_review: { label: 'Créée',    bg: 'bg-blue-100',    text: 'text-blue-800',    accent: '#3b82f6' },
  approved:       { label: 'Créée',    bg: 'bg-blue-100',    text: 'text-blue-800',    accent: '#3b82f6' },
  scheduled:      { label: 'Planifiée', bg: 'bg-amber-100',  text: 'text-amber-800',   accent: '#f59e0b' },
  in_progress:    { label: 'Planifiée', bg: 'bg-amber-100',  text: 'text-amber-800',   accent: '#f59e0b' },
  rejected:       { label: 'Annulée',  bg: 'bg-gray-100',    text: 'text-gray-500',    accent: '#6b7280' }
};

// Fold legacy status values onto the 3-step lean stepper.
const LEAN_STATUS = (s) => {
  if (['created', 'draft', 'submitted', 'manager_review', 'approved'].includes(s)) return 'created';
  if (['planned', 'scheduled', 'in_progress'].includes(s)) return 'planned';
  if (['cancelled', 'rejected'].includes(s)) return 'cancelled';
  return s; // completed
};

const PRIORITY_CONFIG = {
  low: { label: 'Basse', cls: 'bg-gray-100 text-gray-600' },
  medium: { label: 'Moyenne', cls: 'bg-blue-100 text-blue-700' },
  high: { label: 'Haute', cls: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Critique', cls: 'bg-red-100 text-red-700' }
};

const ORIGIN_LABELS = { scheduled: 'Révision planifiée', problem_found: 'Problème constaté', telematics_alert: 'Alerte télématique', inspection: 'Inspection' };
function MaintenanceDetailModal({ request, onClose, onUpdate, onMessage, vehicles, drivers }) {
  // Devise de l'organisation de la demande - un seul outil monetaire. Toutes les
  // lignes de cette modale appartiennent a la meme demande, donc a la meme filiale.
  const { money } = useOrgCurrency();
  const { t, fmt } = useFormat();
  const EUR = (v) => (v ? money(v, request) : '-');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [operations, setOperations] = useState([]);
  const [opsLoading, setOpsLoading] = useState(false);

  // Sub-panel states
  const [showSchedule, setShowSchedule] = useState(false);
  const [showComplete, setShowComplete] = useState(false);

  // Schedule form — scheduled_time default "09:00" (matin typique)
  const [scheduleData, setScheduleData] = useState({
    garage_supplier_id: '',
    scheduled_date: '',
    scheduled_time: '09:00',
    estimated_duration_days: DEFAULT_DURATION
  });
  const [garages, setGarages] = useState([]);

  // Complete form
  const [completeData, setCompleteData] = useState({ invoice_ref: '', odometer: '', notes: '' });
  const [invoiceFile, setInvoiceFile] = useState(null);

  const status = STATUS_CONFIG[request?.status] || STATUS_CONFIG.draft;
  const priority = PRIORITY_CONFIG[request?.priority] || PRIORITY_CONFIG.medium;
  const vehicle = vehicles?.find(v => v.id === request?.vehicle_id);

  // Load operations
  useEffect(() => {
    if (!request?.id) return;
    setOpsLoading(true);
    api.maintenance.getOperations(request.id)
      .then(res => setOperations(res?.data || []))
      .catch(() => {})
      .finally(() => setOpsLoading(false));
  }, [request?.id]);

  // Load garages for schedule panel
  useEffect(() => {
    if (showSchedule && garages.length === 0) {
      api.supplier.getAll()
        .then(res => setGarages((res?.data || []).filter(s => s.category === 'garage')))
        .catch(() => {});
    }
  }, [showSchedule]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!request) return null;

  // Workflow steps — 3-step lean (2026-04-21)
  const steps = [
    { key: 'created',   label: t('maintenance.status.created') },
    { key: 'planned',   label: t('maintenance.status.planned') },
    { key: 'completed', label: t('maintenance.status.completed') }
  ];
  const stepIdx = steps.findIndex(s => s.key === LEAN_STATUS(request.status));

  const doAction = async (actionFn) => {
    setLoading(true);
    try {
      await actionFn();
      onMessage?.({ title: t('maintenance.messages.successTitle'), message: t('maintenance.messages.statusUpdated'), type: 'success' });
      onUpdate?.();
      onClose();
    } catch (err) {
      onMessage?.({ title: t('maintenance.messages.errorTitle'), message: err.message || t('maintenance.messages.actionFailed'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const TABS = [
    { id: 'details', label: t('maintenance.detail.tabDetails'), icon: FileText },
    { id: 'operations', label: t('maintenance.detail.tabOperations', { count: operations.length }), icon: List },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end">
      <div className="bg-white shadow-2xl w-full max-w-5xl h-full overflow-hidden flex flex-col animate-slide-in-right">

        {/* Header — gradient bar like claims */}
        <div className="px-6 py-4 text-white shrink-0" style={{ background: `linear-gradient(135deg, ${status.accent}, ${status.accent}dd)` }}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg" title={t('maintenance.detail.backToList')}>
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Wrench className="w-6 h-6" />
              <div>
                <h2 className="text-lg font-bold">{request.title}</h2>
                <div className="flex items-center gap-2 text-sm opacity-90">
                  <span>#{request.id}</span>
                  <span>•</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${priority.cls}`}>{t(`maintenance.priority.${request?.priority || 'medium'}`, { defaultValue: priority.label })}</span>
                  <span>•</span>
                  <span>{request.origin ? t(`maintenance.origin.${request.origin}`, { defaultValue: ORIGIN_LABELS[request.origin] || request.origin }) : t('maintenance.origin.fallback')}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-white/20">{t(`maintenance.status.${request?.status || 'draft'}`, { defaultValue: status.label })}</span>
              <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {!['rejected', 'cancelled', 'draft'].includes(request.status) && (
          <div className="bg-slate-50 px-6 py-3 border-b flex items-center gap-1 shrink-0">
            {steps.map((step, i) => (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    i < stepIdx ? 'bg-green-500 text-white'
                    : i === stepIdx ? 'text-white' : 'bg-slate-200 text-slate-500'
                  }`} style={i === stepIdx ? { backgroundColor: status.accent } : {}}>
                    {i < stepIdx ? '✓' : i + 1}
                  </div>
                  <span className={`text-[10px] mt-0.5 whitespace-nowrap ${i === stepIdx ? 'font-bold' : 'text-slate-500'}`}
                    style={i === stepIdx ? { color: status.accent } : {}}>{step.label}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 rounded ${i < stepIdx ? 'bg-green-500' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b px-6 flex gap-6 shrink-0">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 py-3 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content — scrollable area */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'details' && (
            <div className="p-6 space-y-5">
              {/* Immobilization banner — derived from planning window (not a status).
                  Shows when today ∈ [scheduled_date, pickup_date] and the request
                  hasn't been closed or cancelled yet. */}
              {(() => {
                if (['completed', 'cancelled', 'rejected'].includes(request.status)) return null;
                if (!vehicle || !request.scheduled_date) return null;
                const today = new Date(); today.setHours(0,0,0,0);
                const start = new Date(request.scheduled_date); start.setHours(0,0,0,0);
                const end = request.pickup_date ? new Date(request.pickup_date) : new Date(start);
                end.setHours(0,0,0,0);
                if (today < start || today > end) return null;
                return (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-purple-600 shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-purple-800">{t('maintenance.detail.immobilizedTitle')}</div>
                      <div className="text-xs text-purple-600">
                        {t('maintenance.detail.immobilizedSince', {
                          plate: vehicle.license_plate,
                          garage: request.garage_name || t('maintenance.detail.garageFallback'),
                          date: fmt.date(start)
                        })}
                        {request.pickup_date && ` ${t('maintenance.detail.immobilizedReturn', { date: fmt.date(end) })}`}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 3-column layout: Vehicle | Garage | Planning */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5" /> {t('maintenance.detail.vehicleSection')}
                  </h3>
                  {vehicle ? (
                    <div className="space-y-1.5">
                      <div className="text-base font-semibold text-slate-800">{vehicle.make} {vehicle.model}</div>
                      <div className="text-sm text-slate-600">{vehicle.license_plate}</div>
                      <div className="text-sm text-slate-500">{vehicle.mileage != null ? t('maintenance.detail.mileage', { mileage: fmt.number(vehicle.mileage) }) : '—'}</div>
                      {vehicle.fuel_type && <div className="text-xs text-slate-400">{vehicle.fuel_type}</div>}
                    </div>
                  ) : <div className="text-slate-400 text-sm">{t('maintenance.detail.vehicleFallback', { id: request.vehicle_id })}</div>}
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" /> {t('maintenance.detail.garageSection')}
                  </h3>
                  {request.garage_name ? (
                    <div className="space-y-1.5">
                      <div className="text-base font-semibold text-slate-800">{request.garage_name}</div>
                      {request.garage_contact && <div className="text-sm text-slate-600">{request.garage_contact}</div>}
                      {request.garage_phone && <div className="text-sm text-slate-500">{request.garage_phone}</div>}
                      {request.garage_email && <div className="text-sm text-slate-500">{request.garage_email}</div>}
                    </div>
                  ) : (
                    <div className="text-slate-400 text-sm italic">{t('maintenance.detail.garageUnassigned')}</div>
                  )}
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-3 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" /> {t('maintenance.detail.planningSection')}
                  </h3>
                  <div className="space-y-1.5 text-sm">
                    <div>
                      <span className="text-slate-500">{t('maintenance.detail.appointmentLabel')}</span>{' '}
                      <span className="text-slate-800">
                        {request.scheduled_date ? fmt.date(request.scheduled_date) : '—'}
                        {request.scheduled_time && ` ${t('maintenance.detail.atTime', { time: request.scheduled_time })}`}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">{t('maintenance.detail.pickupLabel')}</span>{' '}
                      <span className="text-slate-800">
                        {request.pickup_date ? fmt.date(request.pickup_date) : '—'}
                        {request.pickup_time && ` ${t('maintenance.detail.atTime', { time: request.pickup_time })}`}
                      </span>
                    </div>
                    <div><span className="text-slate-500">{t('maintenance.detail.durationLabel')}</span> <span className="text-slate-800">{request.estimated_duration_days ? formatDuration(request.estimated_duration_days) : '—'}</span></div>
                    {request.completed_date && (
                      <div><span className="text-slate-500">{t('maintenance.detail.completedLabel')}</span> <span className="font-medium text-green-700">{fmt.date(request.completed_date)}</span></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Costs — full width card */}
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-5">
                <h3 className="text-xs font-semibold text-amber-700 uppercase mb-3 flex items-center gap-1.5">
                  <Euro className="w-3.5 h-3.5" /> {t('maintenance.detail.financeSection')}
                </h3>
                <div className="grid grid-cols-4 gap-6">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">{t('maintenance.detail.quote')}</div>
                    <div className="text-xl font-bold text-slate-800">{EUR(request.quote_total)}</div>
                    <div className="text-xs text-slate-400">{t('maintenance.detail.operationCount', { count: operations.length })}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">{t('maintenance.detail.invoice')}</div>
                    <div className="text-xl font-bold text-amber-700">{EUR(request.actual_total)}</div>
                    {request.invoice_ref && <div className="text-xs text-slate-400">{t('maintenance.detail.invoiceRef', { ref: request.invoice_ref })}</div>}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">{t('maintenance.detail.gap')}</div>
                    {request.actual_total > 0 && request.quote_total > 0 ? (
                      <div className={`text-xl font-bold ${request.actual_total > request.quote_total ? 'text-red-600' : 'text-green-600'}`}>
                        {request.actual_total > request.quote_total ? '+' : ''}{EUR(request.actual_total - request.quote_total)}
                      </div>
                    ) : <div className="text-xl text-slate-300">—</div>}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">{t('maintenance.detail.initialEstimate')}</div>
                    <div className="text-lg text-slate-500">{EUR(request.estimated_cost)}</div>
                  </div>
                </div>
              </div>

              {/* Description + Notes */}
              {(request.description || request.notes) && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">{t('maintenance.detail.descriptionSection')}</h3>
                  {request.description && <p className="text-sm text-slate-700">{request.description}</p>}
                  {request.notes && <p className="text-sm text-slate-500 mt-2 italic border-t border-slate-200 pt-2">{request.notes}</p>}
                </div>
              )}

              {/* Rejection / Cancellation info */}
              {request.status === 'rejected' && request.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-red-800">{t('maintenance.detail.rejectionReason')}</div>
                  <p className="text-sm text-red-700 mt-1">{request.rejection_reason}</p>
                  {request.rejected_at && <div className="text-xs text-red-500 mt-2">{fmt.dateTime(request.rejected_at)}</div>}
                </div>
              )}
              {request.status === 'cancelled' && request.cancellation_reason && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-800">{t('maintenance.detail.cancellationReason')}</div>
                  <p className="text-sm text-gray-600 mt-1">{request.cancellation_reason}</p>
                  {request.cancelled_at && <div className="text-xs text-gray-400 mt-2">{fmt.dateTime(request.cancelled_at)}</div>}
                </div>
              )}

              {/* Audit timestamps */}
              <div className="text-xs text-slate-400 flex flex-wrap gap-x-6 gap-y-1 pt-2 border-t border-slate-100">
                {request.submitted_at && <span>{t('maintenance.detail.submittedAt', { date: fmt.dateTime(request.submitted_at) })}</span>}
                {request.approved_at && <span>{t('maintenance.detail.approvedAt', { date: fmt.dateTime(request.approved_at) })}</span>}
                {request.started_at && <span>{t('maintenance.detail.startedAt', { date: fmt.dateTime(request.started_at) })}</span>}
                {request.created_at && <span>{t('maintenance.detail.createdAt', { date: fmt.dateTime(request.created_at) })}</span>}
              </div>
            </div>
          )}

          {activeTab === 'operations' && (
            <div className="p-6">
              {opsLoading ? (
                <div className="text-center py-12 text-slate-400 text-sm">{t('maintenance.detail.opsLoading')}</div>
              ) : operations.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <List className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <div className="text-sm">{t('maintenance.detail.opsEmpty')}</div>
                  <div className="text-xs mt-1">{t('maintenance.detail.opsEmptyHint')}</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {operations.map((op, idx) => (
                    <div key={op.id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{op.category_code}</span>
                          <span className="text-sm font-semibold text-slate-800">{op.title}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            op.status === 'completed' ? 'bg-green-100 text-green-700' :
                            op.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                            op.status === 'added_by_garage' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-50 text-blue-600'
                          }`}>{
                            op.status === 'completed' ? t('maintenance.operationStatus.completed') :
                            op.status === 'cancelled' ? t('maintenance.operationStatus.cancelled') :
                            op.status === 'added_by_garage' ? t('maintenance.operationStatus.added_by_garage') : t('maintenance.operationStatus.pending')
                          }</span>
                        </div>
                        {op.description && <div className="text-xs text-slate-500 mt-1">{op.description}</div>}
                      </div>
                      <div className="text-right ml-6 shrink-0">
                        <div className="text-sm text-slate-500">{t('maintenance.detail.quoteLabel')} <span className="font-medium text-slate-700">{EUR(op.quote_amount)}</span></div>
                        {op.actual_amount != null && (
                          <div className="text-sm">{t('maintenance.detail.invoiceLabel')} <span className="font-medium text-amber-700">{EUR(op.actual_amount)}</span></div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Summary row */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex justify-between items-center">
                    <span className="text-sm font-semibold text-amber-800">{t('maintenance.detail.opsTotal', { count: operations.length })}</span>
                    <div className="text-right">
                      <div className="text-sm">{t('maintenance.detail.quoteLabel')} <span className="font-bold text-slate-800">{EUR(operations.reduce((s, o) => s + (parseFloat(o.quote_amount) || 0), 0))}</span></div>
                      {request.actual_total > 0 && (
                        <div className="text-sm">{t('maintenance.detail.invoiceLabel')} <span className="font-bold text-amber-700">{EUR(request.actual_total)}</span></div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sub-panels (inline, above footer) */}
        {showSchedule && (
          <div className="border-t bg-amber-50 p-5 shrink-0">
            <h4 className="text-sm font-semibold text-amber-800 mb-3">{t('maintenance.detail.schedulePanelTitle')}</h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1">{t('maintenance.detail.garageRequired')}</label>
                <select value={scheduleData.garage_supplier_id} onChange={e => setScheduleData(d => ({...d, garage_supplier_id: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                  <option value="">{t('maintenance.detail.garageChoose')}</option>
                  {garages.map(g => <option key={g.id} value={g.id}>{g.name || g.company_name} — {g.city || ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">{t('maintenance.detail.dateRequired')}</label>
                <input type="date" value={scheduleData.scheduled_date} onChange={e => setScheduleData(d => ({...d, scheduled_date: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  {t('maintenance.detail.timeRequired')}
                  <span className="text-slate-400 ml-1">{t('maintenance.detail.timeSlots')}</span>
                </label>
                <input type="time" step="900"
                  value={scheduleData.scheduled_time}
                  onChange={e => setScheduleData(d => ({...d, scheduled_time: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">{t('maintenance.detail.estimatedDuration')}</label>
                <select
                  value={scheduleData.estimated_duration_days}
                  onChange={e => setScheduleData(d => ({...d, estimated_duration_days: parseFloat(e.target.value)}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
                >
                  {DURATION_PRESETS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* Pickup preview + warnings */}
            <div className="mt-3 text-xs">
              {(() => {
                const pickup = computePickupTime(scheduleData.scheduled_time, scheduleData.estimated_duration_days);
                const d = scheduleData.estimated_duration_days;
                const warn = exceedsGarageClosing(scheduleData.scheduled_time, d);
                const arrivalHour = parseInt((scheduleData.scheduled_time || '00:00').split(':')[0], 10);
                const lateDropoff = arrivalHour >= GARAGE_LAST_DROPOFF && d < 1;
                return (
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <span className="text-slate-700">
                      {t('maintenance.detail.pickupExpected')}{' '}
                      <strong>
                        {d < 1
                          ? t('maintenance.detail.pickupSameDay')
                          : d === 1
                            ? t('maintenance.detail.pickupEndOfDay')
                            : t('maintenance.detail.pickupDaysAfter', { count: Math.ceil(d - 1) })}
                      </strong>
                      {pickup && ` ${t('maintenance.detail.pickupAt', { time: pickup })}`}
                    </span>
                    {warn && (
                      <span className="text-red-600 font-medium">
                        {t('maintenance.detail.warnClosing')}
                      </span>
                    )}
                    {lateDropoff && !warn && (
                      <span className="text-amber-700">
                        {t('maintenance.detail.warnLateDropoff')}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowSchedule(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">{t('common.actions.cancel')}</button>
              <button disabled={!scheduleData.scheduled_date || loading}
                onClick={() => doAction(() => api.maintenance.schedule(request.id, scheduleData))}
                className="px-5 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 font-medium">
                {t('maintenance.detail.schedule')}
              </button>
            </div>
          </div>
        )}

        {showComplete && (
          <div className="border-t bg-green-50 p-5 shrink-0">
            <h4 className="text-sm font-semibold text-green-800 mb-3">{t('maintenance.detail.completePanelTitle')}</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1">{t('maintenance.detail.invoiceRefField')}</label>
                <input type="text" value={completeData.invoice_ref} onChange={e => setCompleteData(d => ({...d, invoice_ref: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="FAC-..." />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">{t('maintenance.detail.odometer')}</label>
                <input type="number" value={completeData.odometer} onChange={e => setCompleteData(d => ({...d, odometer: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder={vehicle?.mileage ? t('maintenance.detail.odometerPlaceholder', { mileage: vehicle.mileage }) : ''} />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">{t('maintenance.detail.closingNotes')}</label>
                <input type="text" value={completeData.notes} onChange={e => setCompleteData(d => ({...d, notes: e.target.value}))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder={t('maintenance.detail.closingNotesPlaceholder')} />
              </div>
            </div>
            {/* Invoice file upload — stored under VEHICLE scope / 50_MAINTENANCE_INVOICES */}
            <div className="mt-4">
              <label className="block text-xs text-slate-600 mb-1">
                {t('maintenance.detail.invoiceFile')}
                <span className="text-slate-400 ml-1">{t('maintenance.detail.invoiceFileHint', { plate: vehicle?.license_plate || '' })}</span>
              </label>
              {!invoiceFile ? (
                <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-green-400 hover:text-green-700 cursor-pointer bg-white">
                  <FileText className="w-4 h-4" />
                  <span>{t('maintenance.detail.invoiceFileChoose')}</span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.webp"
                    className="hidden"
                    onChange={e => setInvoiceFile(e.target.files?.[0] || null)}
                  />
                </label>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 bg-white border border-green-300 rounded-lg text-sm">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span className="flex-1 truncate text-slate-700">{invoiceFile.name}</span>
                  <span className="text-xs text-slate-400">{t('maintenance.detail.fileSize', { size: Math.round(invoiceFile.size / 1024) })}</span>
                  <button type="button" onClick={() => setInvoiceFile(null)} className="text-red-500 hover:text-red-700 text-xs">{t('maintenance.detail.remove')}</button>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowComplete(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">{t('common.actions.cancel')}</button>
              <button disabled={loading}
                onClick={() => doAction(async () => {
                  // 1. Upload invoice if provided (VEHICLE scope, MAINTENANCE_INVOICE type)
                  let uploadedName = null;
                  if (invoiceFile && vehicle?.id) {
                    const ref = `VHC_${String(vehicle.id).padStart(6, '0')}`;
                    const up = await api.document.upload(invoiceFile, {
                      scope: 'VEHICLE',
                      reference_id: ref,
                      document_type: 'MAINTENANCE_INVOICE',
                      issue_date: new Date().toISOString().split('T')[0],
                      notes: `Maintenance #${request.id} — ${request.title || ''}`,
                    });
                    if (up?.ok) uploadedName = up.data?.original_filename || invoiceFile.name;
                    else throw new Error(up?.error?.message || t('maintenance.messages.uploadFailed'));
                  }
                  // 2. Close the request with existing ref OR filename fallback
                  const payload = {
                    ...completeData,
                    invoice_ref: completeData.invoice_ref || uploadedName || null,
                  };
                  await api.maintenance.complete(request.id, payload);
                  setInvoiceFile(null);
                })}
                className="px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">
                {t('maintenance.detail.complete')}
              </button>
            </div>
          </div>
        )}

        {/* Footer — action bar (3-step lean workflow) */}
        <div className="border-t px-6 py-3 flex justify-between items-center bg-white shrink-0">
          <div>
            {!['completed', 'cancelled', 'rejected'].includes(request.status) && (
              <button
                onClick={() => doAction(() => api.maintenance.cancel(request.id, 'Annulé par le gestionnaire'))}
                className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                disabled={loading}
              >
                {t('maintenance.detail.cancelRequest')}
              </button>
            )}
          </div>

          <div className="flex gap-2">
            {/* Planifier — fires from 'created' or any legacy pre-plan state */}
            {['created', 'draft', 'submitted', 'manager_review', 'approved'].includes(request.status) && (
              <button disabled={loading} onClick={() => setShowSchedule(true)}
                className="px-5 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 font-medium flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> {t('maintenance.detail.planAppointment')}
              </button>
            )}

            {/* Clôturer — fires from 'planned' or any legacy mid-flight state */}
            {['planned', 'scheduled', 'in_progress'].includes(request.status) && (
              <button disabled={loading} onClick={() => setShowComplete(true)}
                className="px-5 py-2 text-sm bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 font-medium flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4" /> {t('maintenance.detail.complete')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceDetailModal;
